import type { CategoryContent } from '../row.js';

/** Central Asian History, second chunk. Correct option first; rotated at build. */
export const MORE_CENTRAL_ASIA: CategoryContent = {
  msc: [
    [
      'The Kushan Empire is best known for what?',
      [
        'Linking Indian, Iranian and Chinese worlds, and patronising Gandharan Buddhist art',
        'Destroying the Greco-Bactrian kingdoms and expelling Hellenistic rule from the east',
        'Introducing Zoroastrianism as a state religion across Transoxiana',
        'Establishing the first sedentary Turkic polity north of the Syr Darya',
      ],
      0,
      'Kanishka\'s coinage carries Greek, Iranian and Indian deities together, which is about as direct a statement of the empire\'s position as you could ask for.',
    ],
    [
      'Bukhara and Samarkand were centres of which dynasty in the ninth and tenth centuries?',
      [
        'The Samanids',
        'The Ghaznavids, who ruled from Ghazna in modern Afghanistan',
        'The Qarakhanids, whose base lay in the Semirechye',
        'The Buyids, whose power was centred on western Iran',
      ],
      0,
      'The Samanid court is where New Persian literature begins - Rudaki, and the milieu Ferdowsi drew on.',
    ],
    [
      'The Silk Road city of Merv was destroyed in which century?',
      ['The thirteenth', 'The eleventh', 'The fifteenth', 'The ninth'],
      0,
      'Sacked by Tolui in 1221. It had been one of the largest cities in the world and never recovered its position.',
    ],
    [
      'The Chagatai Khanate occupied which region?',
      [
        'Transoxiana and the Tarim basin, between the Golden Horde and the Yuan',
        'The Pontic and Caspian steppe west of the Urals',
        'Iran and Iraq, following the fall of the Abbasid caliphate',
        'Mongolia proper, together with the Manchurian frontier',
      ],
      0,
      'Its later division into western and eastern halves is the setting Timur emerged from, and Chagatai Turkic became a literary language in its own right.',
    ],
    [
      'The Uzbek Shaybanids displaced which dynasty in Transoxiana?',
      [
        'The Timurids',
        'The Safavids, who had briefly annexed Bukhara',
        'The Qarakhanids, then in their final decline',
        'The Golden Horde\'s successor khanates of the lower Volga',
      ],
      0,
      'Muhammad Shaybani took Samarkand in 1500. Babur, dispossessed, went south and founded the Mughal empire instead.',
    ],
    [
      'What was the yam?',
      [
        'The Mongol postal relay system of staged stations and remounts',
        'The tribute assessment levied on conquered sedentary populations',
        'The imperial guard recruited from the sons of subordinate chiefs',
        'The seasonal assembly at which campaign plans were settled',
      ],
      0,
      'Relay riders could cover several hundred kilometres a day. Marco Polo describes it, and it is a large part of why the empire could be governed at all.',
    ],
    [
      'Russian conquest of the Central Asian khanates was largely completed by which decade?',
      ['The 1880s', 'The 1850s', 'The 1900s', 'The 1820s'],
      0,
      'Tashkent fell in 1865, Samarkand in 1868, Khiva in 1873, and the Turkmen resistance ended at Geok Tepe in 1881.',
    ],
    [
      'The Kazakh zhuz divisions refer to what?',
      [
        'The three great hordes - Senior, Middle and Junior',
        'The tax districts imposed by Russian administration after annexation',
        'The seasonal migration routes followed by pastoral households',
        'The clerical hierarchy of the Kazakh steppe under Bukharan influence',
      ],
      0,
      'They submitted to Russian protection at different dates, which is a large part of why annexation proceeded piecemeal over a century.',
    ],
    [
      'The Aral Sea\'s twentieth-century collapse was principally caused by what?',
      [
        'Diversion of the Amu Darya and Syr Darya for irrigated cotton',
        'A regional decline in precipitation attributable to climate change',
        'Tectonic subsidence altering the basin\'s drainage',
        'Evaporation losses from the construction of shallow reservoirs',
      ],
      0,
      'Soviet cotton planning from the 1960s. It is the standard case study of a deliberate, foreseen environmental trade-off rather than an accident.',
    ],
    [
      'Timur\'s campaigns reached which of these cities?',
      [
        'Delhi, sacked in 1398',
        'Cairo, taken after the defeat of the Mamluks',
        'Constantinople, besieged but not captured',
        'Vienna, reached during the western campaign of 1402',
      ],
      0,
      'He also took Damascus and Aleppo, and captured the Ottoman sultan at Ankara in 1402 - but never Cairo or Constantinople.',
    ],
  ],
  phd: [
    [
      'The Battle of Ankara in 1402 had what consequence?',
      [
        'Timur captured Bayezid I, and the Ottoman state fell into a decade-long interregnum',
        'The Ottomans lost their Anatolian possessions permanently to the Timurids',
        'Constantinople fell to a joint Timurid and Ottoman siege',
        'The Mamluks annexed the Ottoman territories in Syria',
      ],
      0,
      'It delayed the fall of Constantinople by half a century - the interregnum is why Byzantium survived until 1453.',
    ],
    [
      'The Golden Horde\'s conversion to Islam is associated with which ruler?',
      [
        'Uzbeg Khan, in the early fourteenth century',
        'Batu, at the time of the western campaigns',
        'Berke, who first professed it without making it official',
        'Tokhtamysh, after his defeat by Timur',
      ],
      0,
      'Berke had converted personally in the 1250s; Uzbeg made it the state religion, which is a different and more consequential act.',
    ],
    [
      'Ulugh Beg is remembered chiefly for what?',
      [
        'His observatory at Samarkand and the star catalogue produced there',
        'His codification of Timurid dynastic law after his grandfather\'s death',
        'His patronage of Chagatai poetry in place of Persian',
        'His campaigns restoring Timurid control over Khorasan',
      ],
      0,
      'The Zij-i Sultani gave sidereal year and axial tilt values that stood as the best available for two centuries.',
    ],
    [
      'The Khanate of Kokand was significant in the nineteenth century as what?',
      [
        'The Ferghana-based state whose expansion brought it into direct collision with Russian advance',
        'The principal Ottoman client polity in Central Asia, receiving arms and recognition from Istanbul',
        'The last surviving Chinggisid dynasty in Transoxiana',
        'The main intermediary in the Bukharan trade with British India',
      ],
      0,
      'It was annexed outright in 1876, unlike Bukhara and Khiva, which survived as protectorates until 1920.',
    ],
    [
      'What was the significance of the 1916 revolt in Turkestan?',
      [
        'A rising against wartime labour conscription, met with severe reprisals and mass flight to China',
        'The first organised Bolshevik seizure of power in the region',
        'A revolt of Russian settlers against requisitioning by the imperial administration',
        'A religious rising led by Sufi orders against the Jadid reformers',
      ],
      0,
      'The Kyrgyz and Kazakh death toll and the flight into Xinjiang make it the largest single episode of violence in the region before collectivisation.',
    ],
    [
      'The Chagatai literary language is associated above all with which poet?',
      [
        'Ali-Shir Nava\'i',
        'Rudaki, at the Samanid court',
        'Jami, writing at Herat',
        'Babur, in his memoirs',
      ],
      0,
      'Nava\'i argued explicitly for Turkic as a literary equal to Persian. Babur wrote the Baburnama in Chagatai, but he is not why the language has status.',
    ],
    [
      'The Transcaspian and Turkestan railways mattered because of what?',
      [
        'They made cotton monoculture and rapid military movement possible together',
        'They opened the region to European rather than Russian manufactured goods',
        'They replaced the caravan routes through Persia to the Gulf',
        'They connected the region directly to the Chinese rail network',
      ],
      0,
      'Rail is what turned a conquest into an economic reorientation: by 1915 the region was importing grain and exporting fibre.',
    ],
    [
      'The Soviet national delimitation of Central Asia in 1924-36 did what?',
      [
        'Replaced the Turkestan and Bukharan units with republics defined on ethno-linguistic lines',
        'Preserved the pre-revolutionary administrative boundaries under new names',
        'Created a single Central Asian federal republic within the USSR',
        'Restored the boundaries of the pre-Russian khanates',
      ],
      0,
      'The borders it drew are the ones independent states inherited in 1991, including the Ferghana valley enclaves.',
    ],
    [
      'Sufi orders in Central Asia were historically most influential through what?',
      [
        'The Naqshbandi network\'s links between religious authority and political power',
        'Control of the region\'s madrasa curricula to the exclusion of legal scholars',
        'Their monopoly over long-distance trade financing',
        'Their role as the principal scribes of the khanates\' chanceries',
      ],
      0,
      'The Naqshbandiyya\'s sober, this-worldly orientation is precisely what made it politically usable, in Transoxiana and in Mughal India alike.',
    ],
    [
      'The Kalmyks are distinctive among the peoples of the Eurasian steppe for what?',
      [
        'Being Oirat Mongols who migrated west and remained Tibetan Buddhist',
        'Being the only Turkic group to retain a Chinggisid ruling house into the modern era',
        'Converting to Orthodox Christianity on entering Russian service',
        'Maintaining an independent khanate in the north Caucasus until 1917',
      ],
      0,
      'They reached the lower Volga in the seventeenth century, which makes them Europe\'s only historically Buddhist people.',
    ],
  ],
  professor: [
    [
      'The "nomadic state formation" debate turns on which question?',
      [
        'Whether steppe polities were driven by internal dynamics or by the need to extract from sedentary neighbours',
        'Whether pastoral nomadism preceded or followed the adoption of settled agriculture across the Eurasian steppe',
        'Whether steppe armies were composed of free herdsmen or of conscripted dependents',
        'Whether steppe successions were determined by primogeniture or by acclamation',
      ],
      0,
      'Barfield\'s mirror-empires argument against Kradin and others. It matters because the extractive reading makes the steppe derivative and the internalist reading does not.',
    ],
    [
      'The Sogdian Ancient Letters, recovered near Dunhuang, are valuable because of what?',
      [
        'They are private commercial and family correspondence',
        'They contain the earliest known Sogdian translation of Buddhist scripture',
        'They record the treaty terms between Sogdiana and the Tang',
        'They preserve a complete legal code in a Middle Iranian language',
      ],
      0,
      'Fourth-century letters about debts, goods and a stranded wife. They tell you how the trade actually worked, which no chronicle does.',
    ],
    [
      'What does the term "Turco-Persian" designate in the historiography of the region?',
      [
        'A composite political culture pairing Turkic military rule with Persianate administration and letters',
        'The linguistic substrate shared by Turkic and Iranian languages of the region',
        'The dynastic intermarriages between the Ottoman and Safavid ruling houses, and the shared court culture that resulted',
        'The bilingual chancery practice of the Russian Turkestan administration',
      ],
      0,
      'Canfield\'s formulation. It describes everything from the Ghaznavids to the Mughals, and explains why Persian was the administrative language of empires whose rulers spoke Turkic.',
    ],
    [
      'The archaeology of Gonur Depe in Turkmenistan is associated with which complex?',
      [
        'The Bactria-Margiana Archaeological Complex of the late third and early second millennia BC',
        'The Andronovo horizon of the northern steppe',
        'The Yaz culture of the early Iron Age, which succeeded it across the same oases of southern Turkmenistan',
        'The Achaemenid provincial administration of Margiana',
      ],
      0,
      'Sarianidi\'s excavations. Whether the BMAC is an indigenous oasis civilisation or an Indo-Iranian intrusion is still not settled.',
    ],
    [
      'The Tarim Basin\'s Tocharian languages are significant to historical linguistics because of what?',
      [
        'They are Indo-European but show closer affinity to western branches than to nearby Indo-Iranian',
        'They constitute the oldest attested branch of the Indo-Iranian family',
        'They preserve a pre-Indo-European substrate otherwise entirely lost from the surviving record of the region',
        'They are the only Indo-European languages written in a Semitic script',
      ],
      0,
      'The centum-satem anomaly: a centum language sitting far east of every other one, which is exactly why the mummies attracted the attention they did.',
    ],
    [
      'The "Pax Mongolica" concept is criticised on what grounds?',
      [
        'It generalises from a short peak of secure trade and understates the conquests\' demographic cost',
        'It attributes to the Mongols a road network that predated them entirely',
        'It relies on Chinese sources that postdate the period concerned by several centuries at least',
        'It confuses the Ilkhanate with the Yuan in matters of commercial policy',
      ],
      0,
      'Both things are true at once - integration and catastrophe - and the term selects one of them, which is why it keeps having to be qualified.',
    ],
    [
      'The Manas epic is significant as what?',
      [
        'A vast Kyrgyz oral epic, whose recorded versions run to hundreds of thousands of lines',
        'The Uzbek dynastic chronicle of the Shaybanid house',
        'A Persian verse history of the Timurid succession, composed at the Herat court under Shah Rukh',
        'The earliest written record of Kazakh customary law',
      ],
      0,
      'Longer than the Iliad and Odyssey combined. Soviet policy toward it swung between promotion as national heritage and suppression as feudal-nationalist.',
    ],
    [
      'The Qing administration of Xinjiang after 1759 is characterised by what?',
      [
        'Indirect rule through local begs under military governors',
        'Immediate incorporation as a regular province on the model of the interior',
        'Administration through the Lifanyuan as a tributary rather than a territory',
        'Delegation of authority to the Kokand khanate as a Qing client',
      ],
      0,
      'Provincial status only came in 1884, after Yaqub Beg\'s rebellion and Zuo Zongtang\'s reconquest.',
    ],
    [
      'The "Inner Asian" versus "Central Asian" terminological distinction usually marks what?',
      [
        'Whether Mongolia, Tibet and Xinjiang are included alongside the former Soviet republics',
        'Whether the region is defined by aridity or by pastoral land use',
        'Whether pre-Islamic or post-Islamic periods are under discussion',
        'Whether the analysis is linguistic or archaeological in method',
      ],
      0,
      'It is not pedantry: the wider frame makes the Qing and the Russian empires comparable cases, and the narrower one does not.',
    ],
    [
      'Vambery\'s and Schuyler\'s nineteenth-century travel accounts are used with caution because of what?',
      [
        'Both were written to inform imperial policy',
        'Neither author reached the cities he described at first hand',
        'Both survive only in heavily edited later translations',
        'Both drew almost entirely on Russian official statistics',
      ],
      0,
      'Vambery travelled in disguise and courted the British establishment; Schuyler was an American diplomat. The observation is often good and the framing never neutral.',
    ],
  ],
};
